/**
 * Reference data for the Cuban Bikes scoring algorithm.
 * Extracted from the scoring Excel and mapped to codebase conventions.
 */

export const ATTRIBUTES = [
  'Speed',
  'Comfort',
  'BatteryRange',
  'AllTerrain',
  'Security',
  'SmartFeatures',
  'Storage',
  'Safety'
];

/**
 * Segment data with populations and attribute preferences (1-5 scale).
 * Preference of 4 or 5 = high priority for that segment.
 * Data extracted from 'Segment Preferences, Ad Costs' Excel sheet.
 */
export const SEGMENTS = {
  'Urban Commuters': {
    population: 1400000,
    preferences: {
      Speed: 2,
      Comfort: 5,
      BatteryRange: 5,
      AllTerrain: 1,
      Security: 5,
      SmartFeatures: 2,
      Storage: 2,
      Safety: 2
    }
  },
  'Tourism Agencies': {
    population: 275000,
    preferences: {
      Speed: 2,
      Comfort: 4,
      BatteryRange: 5,
      AllTerrain: 4,
      Security: 2,
      SmartFeatures: 4,
      Storage: 2,
      Safety: 3
    }
  },
  'Rural': {
    population: 900000,
    preferences: {
      Speed: 3,
      Comfort: 1,
      BatteryRange: 4,
      AllTerrain: 5,
      Security: 2,
      SmartFeatures: 1,
      Storage: 5,
      Safety: 1
    }
  },
  'Delivery/Logistics': {
    population: 225000,
    preferences: {
      Speed: 5,
      Comfort: 1,
      BatteryRange: 3,
      AllTerrain: 2,
      Security: 5,
      SmartFeatures: 2,
      Storage: 5,
      Safety: 2
    }
  },
  'Health/Fitness': {
    population: 650000,
    preferences: {
      Speed: 5,
      Comfort: 5,
      BatteryRange: 1,
      AllTerrain: 3,
      Security: 1,
      SmartFeatures: 5,
      Storage: 1,
      Safety: 3
    }
  },
  'Education': {
    population: 500000,
    preferences: {
      Speed: 2,
      Comfort: 2,
      BatteryRange: 2,
      AllTerrain: 2,
      Security: 5,
      SmartFeatures: 2,
      Storage: 3,
      Safety: 4
    }
  },
  'Government': {
    population: 389000,
    preferences: {
      Speed: 3,
      Comfort: 3,
      BatteryRange: 1,
      AllTerrain: 2,
      Security: 3,
      SmartFeatures: 5,
      Storage: 5,
      Safety: 3
    }
  }
};

export const SEGMENT_NAMES = Object.keys(SEGMENTS);

/**
 * Trade-off matrix: when selecting an attribute at a given level,
 * these penalties are applied to other attributes.
 *
 * Structure: tradeoffs[selectedAttribute][level][affectedAttribute] = penalty
 * Levels 1-3 generally have no or minimal trade-offs.
 * Levels 4-5 have significant penalties to other attributes.
 */
export const TRADEOFFS = {
  Speed: {
    1: {},
    2: {},
    3: { AllTerrain: -1 },
    4: { Comfort: -1, BatteryRange: -1, AllTerrain: -2, Storage: -1 },
    5: { Comfort: -2, BatteryRange: -2, AllTerrain: -3, Storage: -2, Safety: -1 }
  },
  Comfort: {
    1: {},
    2: {},
    3: { AllTerrain: -1 },
    4: { Speed: -1, AllTerrain: -2, Storage: -1 },
    5: { Speed: -2, BatteryRange: -1, AllTerrain: -3, Storage: -2, Safety: -1 }
  },
  BatteryRange: {
    1: {},
    2: {},
    3: {},
    4: { Speed: -1, AllTerrain: -1, Storage: -1 },
    5: { Speed: -2, AllTerrain: -2, Security: -1, SmartFeatures: -1, Storage: -2, Safety: -1 }
  },
  AllTerrain: {
    1: {},
    2: {},
    3: {},
    4: { Speed: -1, BatteryRange: -1, Storage: -1 },
    5: { Speed: -2, Comfort: -1, BatteryRange: -2, Security: -1, Storage: -2, Safety: -1 }
  },
  Security: {
    1: {},
    2: {},
    3: {},
    4: { BatteryRange: -1, Storage: -1 },
    5: { Speed: -1, Comfort: -1, BatteryRange: -2, AllTerrain: -1, Storage: -2, Safety: -1 }
  },
  SmartFeatures: {
    1: {},
    2: {},
    3: {},
    4: { Speed: -1, BatteryRange: -1, Storage: -1 },
    5: { Speed: -2, Comfort: -1, BatteryRange: -2, AllTerrain: -1, Storage: -2, Safety: -1 }
  },
  Storage: {
    1: {},
    2: {},
    3: {},
    4: { Speed: -1, BatteryRange: -1, Security: -1, SmartFeatures: -1 },
    5: { Speed: -2, Comfort: -1, BatteryRange: -2, AllTerrain: -1, Security: -2, SmartFeatures: -2, Safety: -1 }
  },
  Safety: {
    1: {},
    2: {},
    3: {},
    4: { Speed: -1, BatteryRange: -1, SmartFeatures: -1 },
    5: { Speed: -2, Comfort: -1, BatteryRange: -2, AllTerrain: -1, SmartFeatures: -2, Storage: -1 }
  }
};

/**
 * Global scoring parameters.
 */
export const GLOBAL_PARAMS = {
  // Brand spend from Round X applies to rounds with these weights
  brandSpendDistribution: {
    1: { 1: 0.3, 2: 0.3, 3: 0.4 },  // R1 brand spend: 30% R1, 30% R2, 40% R3
    2: { 2: 0.5, 3: 0.5 },          // R2 brand spend: 50% R2, 50% R3
    3: { 3: 1.0 }                    // R3 brand spend: 100% R3
  },

  // Brand spend multipliers for each round
  brandSpendMultipliers: {
    1: { 1: 1, 2: 2, 3: 2 },  // R1 brand: 1x in R1, 2x in R2, 2x in R3
    2: { 2: 1, 3: 2 },       // R2 brand: 1x in R2, 2x in R3
    3: { 3: 1 }              // R3 brand: 1x in R3
  },

  // Discount factor for "All Potential" / "Broad" targeting (15% less effective)
  discountedImpact: 0.85,

  // "All Potential" and "Broad" brand ads don't carry over to future rounds
  broadTargetsNoResidual: true
};
